/**
 * Product configuration loader, demo product, price compute (v4).
 *
 * A config = forme x fabric x leg. Combinations are finite. The demo product
 * below drives the configurator out of the box with procedural geometry.
 */

import type {
  ProductConfig,
  ConfigState,
  FormeDef,
  MaterialOption,
  ColorwayOption,
  FabricSwatch,
} from "@/types";
import { QUATERNIUS_LIVING_ROOM } from "./QuaterniusProduct";

export const DEFAULT_STATE: ConfigState = {
  formeId: "f-3p",
  fabricId: "mat-stone",
  legId: "leg-noir",
  yaw: 35,
  pitch: 18,
  zoom: 1,
};

/**
 * Demo product — modular sofa. Procedural geometry, no GLB needed.
 */
export const POC_PRODUCT: ProductConfig = {
  productId: "demo-modular-sofa",
  productLabel: "Modular Sofa",
  description:
    "Premium modular sofa. Feather-down seating, removable covers, made in France.",
  basePrice: 1490,
  currency: "USD",
  rating: 4.6,
  reviewCount: 124,
  leadTime: "Ships in 6 to 8 weeks",
  formes: [
    {
      id: "f-2p",
      label: "2-Seater",
      featured: true,
      priceDelta: 0,
      modules: [
        { moduleId: "section-arm-left" },
        { moduleId: "section-regular", attachTo: { moduleId: "section-arm-left", socketName: "right" } },
        { moduleId: "section-arm-right", attachTo: { moduleId: "section-regular", socketName: "right" } },
      ],
    },
    {
      id: "f-3p",
      label: "3-Seater",
      featured: true,
      priceDelta: 400,
      modules: [
        { moduleId: "section-arm-left" },
        { moduleId: "section-regular", attachTo: { moduleId: "section-arm-left", socketName: "right" } },
        { moduleId: "section-regular-b", attachTo: { moduleId: "section-regular", socketName: "right" } },
        { moduleId: "section-arm-right", attachTo: { moduleId: "section-regular-b", socketName: "right" } },
      ],
    },
    {
      id: "f-l-left",
      label: "Left Chaise",
      featured: true,
      priceDelta: 900,
      modules: [
        { moduleId: "section-arm-right" },
        { moduleId: "section-regular", attachTo: { moduleId: "section-arm-right", socketName: "left" } },
        { moduleId: "section-corner", attachTo: { moduleId: "section-regular", socketName: "left" } },
        { moduleId: "section-chaise", attachTo: { moduleId: "section-corner", socketName: "front" } },
      ],
    },
    {
      id: "f-l-right",
      label: "Right Chaise",
      featured: true,
      priceDelta: 900,
      modules: [
        { moduleId: "section-arm-left" },
        { moduleId: "section-regular", attachTo: { moduleId: "section-arm-left", socketName: "right" } },
        { moduleId: "section-corner", attachTo: { moduleId: "section-regular", socketName: "right" } },
        { moduleId: "section-chaise", attachTo: { moduleId: "section-corner", socketName: "front" } },
      ],
    },
    {
      id: "f-4p",
      label: "4-Seater",
      priceDelta: 800,
      modules: [
        { moduleId: "section-arm-left" },
        { moduleId: "section-regular", attachTo: { moduleId: "section-arm-left", socketName: "right" } },
        { moduleId: "section-regular-b", attachTo: { moduleId: "section-regular", socketName: "right" } },
        { moduleId: "section-regular-c", attachTo: { moduleId: "section-regular-b", socketName: "right" } },
        { moduleId: "section-arm-right", attachTo: { moduleId: "section-regular-c", socketName: "right" } },
      ],
    },
    {
      id: "f-u",
      label: "U-Shape",
      priceDelta: 1500,
      modules: [
        { moduleId: "section-corner" },
        { moduleId: "section-chaise", attachTo: { moduleId: "section-corner", socketName: "front" } },
        { moduleId: "section-regular", attachTo: { moduleId: "section-corner", socketName: "right" } },
        { moduleId: "section-corner-b", attachTo: { moduleId: "section-regular", socketName: "right" } },
        { moduleId: "section-chaise-b", attachTo: { moduleId: "section-corner-b", socketName: "front" } },
      ],
    },
  ],
  fabrics: [
    { id: "mat-stone", label: "Stone", category: "mat", color: "#cdc6b6" },
    { id: "mat-linen", label: "Linen", category: "mat", color: "#e6dec9" },
    { id: "mat-boucle", label: "Cream Bouclé", category: "mat", color: "#e8dfcf" },
    { id: "mat-clay", label: "Clay", category: "mat", color: "#b3a48f" },
    { id: "satin-forest", label: "Forest Velvet", category: "satin", color: "#3a513f", priceDelta: 220 },
    { id: "satin-night", label: "Night Velvet", category: "satin", color: "#1f2433", priceDelta: 220 },
    { id: "satin-rust", label: "Rust Velvet", category: "satin", color: "#7c4a36", priceDelta: 220 },
    { id: "satin-blue", label: "Blue Velvet", category: "satin", color: "#37506b", priceDelta: 220 },
    { id: "cuir-cognac", label: "Cognac Leather", category: "cuir", color: "#7b4a23", priceDelta: 540 },
    { id: "cuir-black", label: "Black Leather", category: "cuir", color: "#211e1b", priceDelta: 540 },
    { id: "cuir-caramel", label: "Caramel Leather", category: "cuir", color: "#9c6a3c", priceDelta: 540 },
    { id: "cuir-taupe", label: "Taupe Leather", category: "cuir", color: "#6b5d4f", priceDelta: 540 },
  ],
  legs: [
    { id: "leg-noir", label: "Matte Black", color: "#262320" },
    { id: "leg-brass", label: "Brass", color: "#b7903b", priceDelta: 60 },
    { id: "leg-oak", label: "Oak", color: "#b88f5a", priceDelta: 40 },
    { id: "leg-chrome", label: "Chrome", color: "#c8c8c8", priceDelta: 80 },
  ],
  modules: [
    sectionRegular("section-regular"),
    sectionRegular("section-regular-b"),
    sectionRegular("section-regular-c"),
    {
      id: "section-arm-left",
      label: "Left armrest",
      width: 0.4,
      sockets: [{ name: "right", position: { x: 0.2, y: 0, z: 0 } }],
      proceduralKind: "wedge",
      proceduralColor: "#cdc6b6",
    },
    {
      id: "section-arm-right",
      label: "Right armrest",
      width: 0.4,
      sockets: [{ name: "left", position: { x: -0.2, y: 0, z: 0 } }],
      proceduralKind: "wedge",
      proceduralColor: "#cdc6b6",
    },
    sectionCorner("section-corner"),
    sectionCorner("section-corner-b"),
    sectionChaise("section-chaise"),
    sectionChaise("section-chaise-b"),
  ],
};

function sectionRegular(id: string) {
  return {
    id,
    label: "Seat",
    width: 0.9,
    sockets: [
      { name: "left", position: { x: -0.45, y: 0, z: 0 } },
      { name: "right", position: { x: 0.45, y: 0, z: 0 } },
    ],
    proceduralKind: "box" as const,
    proceduralColor: "#cdc6b6",
  };
}

function sectionCorner(id: string) {
  return {
    id,
    label: "Corner",
    width: 0.9,
    sockets: [
      { name: "left", position: { x: -0.45, y: 0, z: 0 } },
      { name: "right", position: { x: 0.45, y: 0, z: 0 } },
      { name: "front", position: { x: 0, y: 0, z: 0.425 } },
    ],
    proceduralKind: "box" as const,
    proceduralColor: "#cdc6b6",
  };
}

function sectionChaise(id: string) {
  return {
    id,
    label: "Chaise",
    width: 0.85,
    sockets: [
      { name: "back", position: { x: 0, y: 0, z: -0.7 } },
      { name: "front", position: { x: 0, y: 0, z: 0.7 } },
    ],
    proceduralKind: "chaise" as const,
    proceduralColor: "#cdc6b6",
  };
}

/**
 * Resolve which product config to use:
 *   1. window.__NEOVAA_CONFIG__ (host page injection)
 *   2. ?product=quaternius / ?layout= (built-in alt product)
 *   3. ?config=<url> query param
 *   4. default: the real Bangalow (Koala) product config
 *   5. last resort: the procedural POC_PRODUCT (only if the default fetch fails)
 */
export async function loadProductConfig(): Promise<ProductConfig> {
  if (typeof window !== "undefined" && window.__NEOVAA_CONFIG__) {
    return validateConfig(window.__NEOVAA_CONFIG__);
  }
  const params = new URLSearchParams(window.location.search);
  // Built-in alternative product: ?product=quaternius (or ?product=quaternius-living-room)
  // OR any ?layout=<id> param implicitly selects the Quaternius product since the
  // POC sofa uses ?f= keys, not ?layout=.
  const productId = params.get("product");
  if (
    productId === "quaternius" ||
    productId === "quaternius-living-room" ||
    (params.get("layout") !== null && !productId)
  ) {
    return validateConfig(QUATERNIUS_LIVING_ROOM);
  }
  const configURL = params.get("config");
  if (configURL) {
    try {
      const res = await fetch(configURL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as ProductConfig;
      return validateConfig(json);
    } catch (e) {
      console.warn("Failed to load product config from", configURL, e, "— falling back to default");
    }
  }
  // 3b. Embed path: ?nvslug=<slug> with no explicit ?config — load the TRUSTED config
  //     for that slug from get-configurator (single source of truth; it carries the
  //     Shopify binding so an embedded viewer is Shopify-sourced, not the demo default).
  const nvslug = params.get("nvslug");
  if (nvslug) {
    try {
      const res = await fetch(
        `https://vmvbvbrcdphknjibekgg.supabase.co/functions/v1/get-configurator?slug=${encodeURIComponent(nvslug)}`,
      );
      if (res.ok) return validateConfig((await res.json()) as ProductConfig);
    } catch (e) {
      console.warn("Failed to load config for nvslug", nvslug, e, "— falling back to default");
    }
  }
  // 4. Default to the Bungalow studio product (full shape × size × colorway matrix,
  //    per-colorway baked GLBs + studio lighting), so a bare URL (no ?config= and no
  //    host injection) shows the ACTUAL flagship product — never the procedural cube.
  //    Replaced the old single-GLB tinted Bangalow demo (Alex 2026-06-11: total
  //    replacement; pricing stays placeholder until a shop is connected).
  try {
    const res = await fetch("/perf-assets/bungalow-studio/bungalow-studio.json");
    if (res.ok) return validateConfig((await res.json()) as ProductConfig);
  } catch (e) {
    console.warn("Failed to load default Bungalow config", e, "— falling back to POC");
  }
  // 5. Absolute last resort (default fetch failed): the procedural demo product.
  return POC_PRODUCT;
}

/**
 * Basic structural validation. Throws if obviously broken.
 */
export function validateConfig(cfg: ProductConfig): ProductConfig {
  if (!cfg || typeof cfg !== "object") {
    throw new Error("Product config must be an object");
  }
  if (!Array.isArray(cfg.formes) || cfg.formes.length === 0) {
    throw new Error("Product config must have at least one forme");
  }
  // fabrics + legs are OPTIONAL axes (a non-upholstered product like a lamp has
  // neither) — require an array but allow empty. formes + modules stay mandatory so a
  // config that genuinely can't render still fails validation and falls back safely.
  if (!Array.isArray(cfg.fabrics)) {
    throw new Error("Product config fabrics must be an array");
  }
  if (!Array.isArray(cfg.legs)) {
    throw new Error("Product config legs must be an array");
  }
  if (!Array.isArray(cfg.modules) || cfg.modules.length === 0) {
    throw new Error("Product config must have at least one module");
  }
  return cfg;
}

/**
 * Resolve the concrete forme (the GLB/assembly to build) from the INDEPENDENT
 * Shape and Size axes (v50). The grid is `product.formes`, each cell tagged with
 * `shapeId` + `sizeId`. Picking a shape keeps the current size and vice-versa;
 * this function recomputes the matching forme.
 *
 * Resolution order (graceful fallback so nothing ever breaks):
 *   1. exact (shapeId, sizeId) match
 *   2. first forme matching shapeId (size axis absent / no Large variant authored)
 *   3. first forme matching sizeId (e.g. before a shape is chosen)
 *   4. the product's first forme
 *
 * Backward-compat: a product without `shapes`/tagged formes returns the forme
 * whose id === shapeId (the v4 path where formes ARE shapes), else the first.
 */
export function resolveForme(
  product: ProductConfig,
  shapeId: string | undefined,
  sizeId: string | undefined,
): FormeDef {
  const formes = product.formes;
  // v50 grid path: formes carry shapeId tags.
  const hasGrid = formes.some((f) => f.shapeId != null);
  if (hasGrid) {
    if (shapeId != null && sizeId != null) {
      const exact = formes.find((f) => f.shapeId === shapeId && f.sizeId === sizeId);
      if (exact) return exact;
    }
    if (shapeId != null) {
      const byShape = formes.find((f) => f.shapeId === shapeId);
      if (byShape) return byShape;
    }
    if (sizeId != null) {
      const bySize = formes.find((f) => f.sizeId === sizeId);
      if (bySize) return bySize;
    }
    return formes[0];
  }
  // v4 / single-axis path: a forme IS a shape; shapeId is the forme id.
  return formes.find((f) => f.id === shapeId) ?? formes[0];
}

/**
 * Compute the live price for the current config state.
 */
export function computePrice(product: ProductConfig, state: ConfigState): number {
  let price = product.basePrice;
  const forme = product.formes.find((f) => f.id === state.formeId);
  if (forme?.priceDelta) price += forme.priceDelta;
  // Size delta (v49). The forme delta already covers the seat-count variant, so
  // a Size that maps 1:1 to its own forme uses the forme delta; sizes carry an
  // optional extra delta only when they are not themselves the primary axis.
  const size = product.sizes?.find((s) => s.id === state.sizeId);
  if (size?.priceDelta) price += size.priceDelta;
  // Two-level fabric (v49). When `materials` is present it is the source of
  // truth for fabric pricing; the flat `fabrics[]` delta is the single-axis
  // fallback (mutually exclusive — a product uses one model or the other).
  if (product.materials && (state.materialId || state.colorwayId)) {
    const mat = product.materials.find((m) => m.id === state.materialId);
    if (mat?.priceDelta) price += mat.priceDelta;
    const cw = mat?.colorways.find((c) => c.id === state.colorwayId);
    if (cw?.priceDelta) price += cw.priceDelta;
  } else {
    const fabric = product.fabrics.find((f) => f.id === state.fabricId);
    if (fabric?.priceDelta) price += fabric.priceDelta;
  }
  const leg = product.legs.find((g) => g.id === state.legId);
  if (leg?.priceDelta) price += leg.priceDelta;
  return price;
}

/**
 * Lower a (material, colorway) pair onto the engine's single FabricSwatch.
 * The engine's applyFabric already takes { color, textureUrl } — color is the
 * zero-byte tint, textureUrl swaps the weave map (absent → keep the GLB map).
 * This is the bridge that keeps the engine unchanged while the mobile UI uses
 * the two-level model.
 */
export function swatchFor(material: MaterialOption, colorway: ColorwayOption): FabricSwatch {
  return {
    id: colorway.id,
    label: `${material.label} ${colorway.label}`,
    category: "mat",
    color: colorway.color,
    textureUrl: material.textureUrl,
    glb: colorway.glb,
    glbByForme: colorway.glbByForme,
    studio: colorway.studio,
  };
}

/** Itemised price breakdown for the rail "price breakdown" panel. */
export interface PriceLine {
  label: string;
  amount: number;
}

export function priceBreakdown(product: ProductConfig, state: ConfigState): PriceLine[] {
  const lines: PriceLine[] = [{ label: "Base", amount: product.basePrice }];
  const forme = product.formes.find((f) => f.id === state.formeId);
  if (forme?.priceDelta) lines.push({ label: `${forme.label} shape`, amount: forme.priceDelta });
  const size = product.sizes?.find((s) => s.id === state.sizeId);
  if (size?.priceDelta) lines.push({ label: `${size.label} size`, amount: size.priceDelta });
  if (product.materials && (state.materialId || state.colorwayId)) {
    const mat = product.materials.find((m) => m.id === state.materialId);
    if (mat?.priceDelta) lines.push({ label: mat.label, amount: mat.priceDelta });
    const cw = mat?.colorways.find((c) => c.id === state.colorwayId);
    if (cw?.priceDelta) lines.push({ label: cw.label, amount: cw.priceDelta });
  } else {
    const fabric = product.fabrics.find((f) => f.id === state.fabricId);
    if (fabric?.priceDelta) lines.push({ label: fabric.label, amount: fabric.priceDelta });
  }
  const leg = product.legs.find((g) => g.id === state.legId);
  if (leg?.priceDelta) lines.push({ label: `${leg.label} legs`, amount: leg.priceDelta });
  return lines;
}

export function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}
